import { AvatarState } from './avatar.reducer';
import { createSelector, createFeatureSelector } from '@ngrx/store';
import { getSelectedAvatar } from '../config/config.selectors';
import { ProjectSummary } from '../project/models/project-summary.model';
import { getActiveSchema } from '../schema/schema.selectors';
import { Schema } from 'src/app/admin/models/schema.model';
import { AppStore } from '../store.model';
import { getFormattedHumanOSVersion } from './helpers/get-formatted-human-os-version';
import { Avatar } from 'src/app/project-config/components/project-edit-form/models/avatar.model';

export const getAvatarState = createFeatureSelector<AvatarState>('avatar');

export const getAvatarByName = createSelector(
  getAvatarState,
  (state: AvatarState, props: { avatar: string }) =>
    state.avatars.find((avatar) => avatar.name === props.avatar),
);

export const getAvatarRequestError = createSelector(
  getAvatarState,
  (state: AvatarState) => state.error,
);

export const getAvatars = createSelector(getAvatarState, (state: AvatarState) => state.avatars);

export const getCustomerDPs = createSelector(getAvatars, (avatars) =>
  avatars.filter((avatar) => avatar.customerDP),
);

export const getStockDPs = createSelector(getAvatars, (avatars) =>
  avatars.filter((avatar) => !avatar.customerDP),
);

export const getSelectedAvatarDisplayName = createSelector(
  getAvatars,
  getSelectedAvatar,
  (avatars: Avatar[], avatar: string) => {
    if (!avatar) {
      return null;
    }

    const match = avatars.find((item) => item.name === avatar);
    if (!match) {
      return null;
    }

    return match.displayName;
  },
);

export const getProjectAvatarImage = createSelector(
  getAvatars,
  (avatars: Avatar[], props: { project: ProjectSummary }) => {
    const { project } = props;
    if (!project) {
      return null;
    }

    const avatar = avatars.find((item) => project.avatar === item.name);

    return avatar && avatar.imageSrc;
  },
);

export const getActiveAvatars = createSelector<AppStore, Schema, Avatar[]>(
  getActiveSchema,
  (schema) =>
    schema.values.definitions.concreteAvatar.oneOf
      .filter((avatar) => avatar.allOf[1].properties.avatar.enum[0])
      .map((avatar) => {
        const name = avatar.allOf[1].properties.avatar.enum[0];
        const meta = avatar.allOf[1].meta;
        const formattedHumanOSVersion = getFormattedHumanOSVersion(meta.humanOSVersion);
        return Object.assign({}, { name, formattedHumanOSVersion }, meta);
      }),
);
